import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button, message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useRiskCalculator } from './hooks/useRiskCalculator';
import { useCalculateRisks } from './hooks/useRiskCalculatorData';
import { transformApiResponseToResultRows } from './utils/transformers';
import StepWizard from './components/StepWizard';
import ResetButton from './components/ResetButton';
import Step1Cultures from './components/steps/Step1Cultures';
import Step2TreatmentType from './components/steps/Step2TreatmentType';
import Step3ApplicationTechnique from './components/steps/Step3ApplicationTechnique';
import Step4CommercialProducts from './components/steps/Step4CommercialProducts';
import ResultsScreen from './components/results/ResultsScreen';
import { STEPS } from './constants';
import { BackgroundSection } from '@client/feature/activeSubstance/partials';
import CalculatorBackground from '../../../img/calculator_bg.svg';
import CalculateIcon from '../../../img/client/icons/calculate_action.svg';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { usePageMeta } from '@client/hooks/usePageMeta';

/**
 * RiskCalculator Main Component
 * Multi-step calculator for assessing pesticide risk indices
 * Follows Figma design specifications
 */
const RiskCalculator = () => {
  const { t } = useTranslation('riskCalculator');
  usePageMeta({
    title: t('seo|riskCalculator.pageTitle'),
    description: t('seo|riskCalculator.pageDescription'),
    breadcrumbTitle: t('breadcrumb|riskCalculator.title'),
    breadcrumbDescription: t('breadcrumb|riskCalculator.description'),
  });

  // Results state - component local, NOT in URL
  const [results, setResults] = React.useState(null);

  // State management with URL synchronization
  const {
    currentStep,
    currentView,
    selections,
    enrichedData,
    isLoadingLabels, // Loading state for URL cold start
    goToStep,
    goToNextStep,
    goToPreviousStep,
    goToResults,
    goToForm,
    setCultures,
    setTreatmentType,
    setApplicationTechnique,
    setCommercialProducts,
    reset,
    isStepValid,
    canProceedToNextStep,
    canCalculate,
    isFirstStep,
    isLastStep,
  } = useRiskCalculator();

  // Track if we're transitioning to results (prevents race conditions)
  const isTransitioningToResults = React.useRef(false);

  // Handle step click
  const handleStepClick = useCallback(
    (step) => {
      goToStep(step);
    },
    [goToStep]
  );

  // Handle reset with confirmation
  const handleReset = useCallback(() => {
    reset();
    setResults(null);
  }, [reset]);

  // URL validation: redirect to form if view=results but no results data
  // Skip validation if we're in the middle of transitioning
  React.useEffect(() => {
    if (isTransitioningToResults.current) {
      return;
    }
    if (currentView === 'results' && (!results || results.length === 0)) {
      message.warning(t('validation_complete_previous_steps'));
      goToForm();
    }
  }, [currentView, results, goToForm, t]);

  // Calculate risks mutation
  const { mutate: calculateRisks, isPending: isCalculating } =
    useCalculateRisks({
      onSuccess: (data) => {
        // Set flag to prevent validation effect from redirecting
        isTransitioningToResults.current = true;

        // Transform raw API data into ResultRow objects using shared utility
        const resultRows = transformApiResponseToResultRows(
          data,
          selections,
          enrichedData
        );
        setResults(resultRows);

        // Navigate to results view
        goToResults();
        message.success(t('calculation_complete'));

        // Reset flag after navigation completes
        setTimeout(() => {
          isTransitioningToResults.current = false;
        }, 100);
      },
      onError: (error) => {
        console.error('Risk calculation error:', error);
        message.error(
          error.response?.data?.errors?.[0] ||
            error.message ||
            t('calculation_error')
        );
      },
    });

  // Handle calculate button click
  const handleCalculate = useCallback(() => {
    if (!canCalculate) return;

    const payload = {
      cultures: selections.cultures,
      treatmentType: selections.treatmentType,
      applicationTechnique: selections.applicationTechnique,
      commercialProducts: selections.commercialProducts,
    };

    calculateRisks(payload);
  }, [canCalculate, selections, calculateRisks]);

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case STEPS.CULTURES:
        return (
          <div className="step-content">
            <Step1Cultures
              selectedCultures={selections.cultures}
              onSelectCultures={setCultures}
              enrichedCultures={[
                ...(enrichedData.cultures || []),
                ...(enrichedData.cultureChildren || []),
              ]}
            />
          </div>
        );

      case STEPS.TREATMENT_TYPE:
        return (
          <div className="step-content">
            <Step2TreatmentType
              selectedTreatmentType={selections.treatmentType}
              onSelectTreatmentType={setTreatmentType}
              selections={selections}
              enrichedData={enrichedData}
            />
          </div>
        );

      case STEPS.APPLICATION_TECHNIQUE:
        return (
          <div className="step-content">
            <Step3ApplicationTechnique
              selectedApplicationTechnique={selections.applicationTechnique}
              onSelectApplicationTechnique={setApplicationTechnique}
              selections={selections}
              enrichedData={enrichedData}
            />
          </div>
        );

      case STEPS.COMMERCIAL_PRODUCTS:
        return (
          <div className="step-content">
            <Step4CommercialProducts
              selectedProducts={selections.commercialProducts}
              onSelectProducts={setCommercialProducts}
              selections={selections}
              enrichedData={enrichedData}
            />
          </div>
        );

      default:
        // Defense-in-depth: This should never be reached due to URL validation in the hook
        // But return cultures step content as a safe fallback
        return (
          <div className="step-content">
            <Step1Cultures
              selectedCultures={selections.cultures}
              onSelectCultures={setCultures}
              enrichedCultures={[
                ...(enrichedData.cultures || []),
                ...(enrichedData.cultureChildren || []),
              ]}
            />
          </div>
        );
    }
  };

  // Render results screen when view=results
  if (currentView === 'results' && results && results.length > 0) {
    return (
      <div className="relative w-full">
        <div className="container px-4 sm:px-6 lg:px-8">
          <BackgroundSection
            backgroundSource={CalculatorBackground}
            showBottomBg={false}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex flex-col gap-6 sm:gap-8 relative z-10">
              <div className="filters-container">
                <ResultsScreen results={results} setResults={setResults} />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Render form view (default)
  return (
    <div className="relative w-full">
      {/* Main Content */}
      <div className="container px-4 sm:px-6 lg:px-8">
        <BackgroundSection
          backgroundSource={CalculatorBackground}
          showBottomBg={false}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col gap-6 sm:gap-8 relative z-10">
            <div className="filters-container">
              <Spin
                prefixCls="step-wizard"
                spinning={isLoadingLabels || isCalculating}
                tip={isLoadingLabels ? t('loading') : t('calculating')}
              >
                {/* Header with title and reset button */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 mb-4">
                  <h2 className="text-xl sm:text-2xl lg:text-3xl normal-case font-medium m-0">
                    {t('title')}
                  </h2>
                  <ResetButton
                    onReset={handleReset}
                    disabled={
                      currentStep === STEPS.CULTURES &&
                      selections.cultures.length === 0
                    }
                  />
                </div>

                {/* Step Wizard */}
                <StepWizard
                  currentStep={currentStep}
                  onStepClick={handleStepClick}
                  isStepValid={isStepValid}
                  selections={selections}
                  enrichedData={enrichedData}
                />

                {/* Step Content */}
                {renderStepContent()}

                {/* Navigation Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 justify-center items-stretch sm:items-center mt-6">
                  {!isFirstStep && (
                    <Button
                      type="default"
                      onClick={goToPreviousStep}
                      className="w-full sm:w-auto h-11 sm:h-[50px] px-4 sm:px-6 text-sm sm:text-base"
                      icon={<ArrowLeftOutlined />}
                    >
                      {t('button_previous')}
                    </Button>
                  )}

                  {!isLastStep && (
                    <Button
                      type="primary"
                      onClick={goToNextStep}
                      disabled={!canProceedToNextStep}
                      className="w-full sm:w-auto h-11 sm:h-[50px] px-4 sm:px-6 text-sm sm:text-base"
                      icon={<ArrowRightOutlined />}
                      iconPosition="end"
                      style={{
                        cursor: canProceedToNextStep
                          ? 'pointer'
                          : 'not-allowed',
                      }}
                    >
                      {t('button_next')}
                    </Button>
                  )}

                  {isLastStep && (
                    <Button
                      type="primary"
                      className="w-full sm:w-auto h-11 sm:h-[50px] px-4 sm:px-6 text-sm sm:text-base"
                      onClick={handleCalculate}
                      disabled={!canCalculate || isCalculating}
                      icon={
                        <img src={CalculateIcon} alt="" className="w-5 h-5" />
                      }
                      style={{
                        cursor:
                          canCalculate && !isCalculating
                            ? 'pointer'
                            : 'not-allowed',
                      }}
                    >
                      {isCalculating ? t('calculating') : t('button_calculate')}
                    </Button>
                  )}
                </div>
              </Spin>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RiskCalculator;
